import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { BlockPlotModelT } from "../../schema/block/plot";
import { useDashboardEditorContext } from "./editor";
import { useDashboardEditorState } from "../store/editor";
import usePrevious from "@ui/lib/hooks/usePrevious";
import type { BlockImageModelT } from "../../schema/block/image";
import type { BlockDiagramModelT } from "../../schema/block/diagram";
import type { BlockPresetModelT } from "../../schema/block/preset";

export const GUIEditorContext = createContext<ReturnType<typeof useCreateGUIEditorContext>>({
  block: {} as BlockPlotModelT,
  preset: "line",
  focusEncoding: () => {},
  focusedEncoding: null,
});

export const useCurrentEditorBlock = <
  T extends BlockPlotModelT | BlockImageModelT | BlockDiagramModelT | BlockPresetModelT | undefined,
>(
  kind: "PLOT" | "IMAGE" | "DIAGRAM" | "PRESET",
) => {
  const editor = useDashboardEditorContext();
  const editorState = useDashboardEditorState(editor?.store);
  return useMemo(
    () =>
      editorState?.blocks.find(b => editor?.block?.kind === kind && b.id === editor?.block?.id) as
        | T
        | undefined,
    [editor?.block?.id, editor?.block?.kind, editorState?.blocks, kind],
  );
};

export const useGUIEditorContext = () => useContext(GUIEditorContext) ?? {};

export const useCreateGUIEditorContext = (preset: "scatter" | "line") => {
  const editor = useDashboardEditorContext();
  const block = useCurrentEditorBlock<BlockPlotModelT>("PLOT");
  const [focusedEncoding, setFocusedEncoding] = useState<{
    identifier: string;
    column: string;
    layer: number;
    axis: "x" | "y";
    index: number;
    /** @note used for panel animation w/ swap value */
    order: number;
  } | null>();

  const previousBlock = usePrevious(editor?.block);
  useEffect(() => {
    /** @note reset focus encoding between plot selection */
    if (previousBlock?.id !== editor?.block?.id) {
      setFocusedEncoding(null);
    }
  }, [editor?.block, previousBlock?.id]);

  return useMemo(
    () => ({
      block,
      preset,
      focusEncoding: setFocusedEncoding,
      focusedEncoding,
    }),
    [block, focusedEncoding, preset],
  );
};
